import type { ReactNode } from 'react';

/**
 * Shared presentation primitives. Keep the application's sections, fields and
 * action rows visually consistent instead of relying on ad-hoc margins.
 */
export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`ui-section${className ? ` ${className}` : ''}`}>
      {(title || description || actions) && (
        <div className="ui-section-head">
          <div className="ui-section-heading">
            {title && <h3 className="ui-section-title">{title}</h3>}
            {description && <p className="ui-section-description">{description}</p>}
          </div>
          {actions && <div className="ui-section-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`ui-field${className ? ` ${className}` : ''}`}>
      <span className="ui-field-label">{label}</span>
      {children}
      {hint && <span className="ui-field-hint">{hint}</span>}
    </label>
  );
}

export function FieldGrid({ children }: { children: ReactNode }) {
  return <div className="ui-field-grid">{children}</div>;
}

export function ActionRow({ children }: { children: ReactNode }) {
  return <div className="ui-actions">{children}</div>;
}

export function ClearIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M4.5 4.5l7 7m0-7l-7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function IconButton({
  label,
  onClick,
  disabled,
  children,
  className,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`button icon${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
